// src/pages/Contacts.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  InputGroup,
  ButtonGroup,
  Table,
  Badge,
  Modal,
} from "react-bootstrap";

const LOCAL_STORAGE_KEY = "callbunker_contacts";

const normalizePhone = (phone) => {
  if (!phone) return "";
  return phone.replace(/[^\d+]/g, "").replace(/^00/, "+");
};

// Global function for Flutter bridge
window.getContacts = async () => {
  if (window.flutter_inappwebview) {
    try {
      const raw = await window.flutter_inappwebview.callHandler("getContacts");
      return (raw || []).map((c) => ({
        name: c.name || "Unknown",
        phone: normalizePhone(c.phone),
      }));
    } catch (err) {
      console.error("getContacts error:", err);
      return [];
    }
  }
  return [];
};

const Contacts = () => {
  // Active Tab: 'all' | 'trusted' | 'blocked'
  const [activeTab, setActiveTab] = useState("all");

  // Device Contacts State
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  // Whitelist State
  const [whitelist, setWhitelist] = useState([]);
  const [showAddWhitelistModal, setShowAddWhitelistModal] = useState(false);
  const [newWhitelistContact, setNewWhitelistContact] = useState({ phoneNumber: "", name: "" });

  // Blacklist State
  const [blacklist, setBlacklist] = useState([]);
  const [showAddBlacklistModal, setShowAddBlacklistModal] = useState(false);
  const [newBlacklistContact, setNewBlacklistContact] = useState({ phoneNumber: "", reason: "" });

  const navigate = useNavigate();

  // Load from localStorage on mount & fetch lists
  useEffect(() => {
    const saved = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (saved) {
      try {
        setContacts(JSON.parse(saved));
      } catch (e) {
        console.error("Failed to parse saved contacts");
      }
    }
    setLoading(false);
    fetchWhitelist();
    fetchBlacklist();
  }, []);

  // Save to localStorage whenever contacts change
  useEffect(() => {
    if (contacts.length > 0) {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(contacts));
    }
    setFilteredContacts(contacts);
  }, [contacts]);

  // Live search filter for device contacts
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredContacts(contacts);
      return;
    }

    const lowerTerm = searchTerm.toLowerCase();
    const filtered = contacts.filter(
      (contact) =>
        (contact.name || "").toLowerCase().includes(lowerTerm) ||
        (contact.phone || "").includes(searchTerm)
    );
    setFilteredContacts(filtered);
  }, [searchTerm, contacts]);

  const fetchWhitelist = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/whitelist", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWhitelist(res.data.whitelist || []);
    } catch (err) {
      console.error("Error fetching whitelist:", err);
    }
  };

  const fetchBlacklist = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/api/blacklist", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlacklist(res.data.blacklist || []);
    } catch (err) {
      console.error("Error fetching blacklist:", err);
    }
  };

  const syncContacts = async () => {
    setSyncing(true);
    setMessage("");

    const deviceContacts = await window.getContacts();

    if (deviceContacts.length === 0) {
      setMessage("No contacts found. Please allow Contacts permission in phone settings.");
      setMessageType("warning");
      setSyncing(false);
      return;
    }

    const unique = Array.from(
      new Map(deviceContacts.map((c) => [c.phone, c])).values()
    );

    setContacts(unique);
    setMessage(`Success! Imported ${unique.length} contacts`);
    setMessageType("success");
    setSyncing(false);
    setTimeout(() => setMessage(""), 4000);
  };

  const callContact = (phone) => {
    navigate(`/make-call?number=${encodeURIComponent(phone)}`);
  };

  const textContact = (phone) => {
    navigate(`/messages/${encodeURIComponent(phone)}`);
  };

  const addToWhitelist = async (phone, name = "") => {
    try {
      const token = localStorage.getItem("token");
      let phoneNumber = normalizePhone(phone);
      if (!phoneNumber.startsWith("+")) {
        phoneNumber = "+1" + phoneNumber;
      }

      // Check if already in blacklist -> move
      const isBlacklisted = blacklist.some(
        (b) => normalizePhone(b.phoneNumber) === phoneNumber
      );

      if (isBlacklisted) {
        await axios.post(
          "/api/blacklist/move-to-whitelist",
          { phoneNumber, name },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage("Removed from Blocked and added to Trusted Contacts");
      } else {
        await axios.post(
          "/api/whitelist",
          { phoneNumber, name },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage("Added to Trusted Contacts successfully");
      }

      setMessageType("success");
      fetchWhitelist();
      fetchBlacklist();
      setTimeout(() => setMessage(""), 3500);
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to add to Trusted Contacts");
      setMessageType("danger");
    }
  };

  const addToBlacklist = async (phone, reason = "Manually blocked") => {
    try {
      const token = localStorage.getItem("token");
      let phoneNumber = normalizePhone(phone);
      if (!phoneNumber.startsWith("+")) {
        phoneNumber = "+1" + phoneNumber;
      }

      await axios.post(
        "/api/blacklist",
        { phoneNumber, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage("Contact blocked successfully");
      setMessageType("warning");
      fetchWhitelist();
      fetchBlacklist();
      setTimeout(() => setMessage(""), 3500);
    } catch (err) {
      setMessage(err.response?.data?.message || "Failed to block contact");
      setMessageType("danger");
    }
  };

  const removeWhitelist = async (phoneNumber) => {
    if (!window.confirm("Remove this contact from Trusted Contacts?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/whitelist/${encodeURIComponent(phoneNumber)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Removed from Trusted Contacts");
      setMessageType("info");
      fetchWhitelist();
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage("Failed to remove number");
      setMessageType("danger");
    }
  };

  const removeBlacklist = async (phoneNumber) => {
    if (!window.confirm("Unblock this contact?")) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/blacklist/${encodeURIComponent(phoneNumber)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage("Contact unblocked successfully");
      setMessageType("info");
      fetchBlacklist();
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      setMessage("Failed to unblock number");
      setMessageType("danger");
    }
  };

  const handleManualAddWhitelist = async (e) => {
    e.preventDefault();
    await addToWhitelist(newWhitelistContact.phoneNumber, newWhitelistContact.name);
    setNewWhitelistContact({ phoneNumber: "", name: "" });
    setShowAddWhitelistModal(false);
  };

  const handleManualAddBlacklist = async (e) => {
    e.preventDefault();
    await addToBlacklist(newBlacklistContact.phoneNumber, newBlacklistContact.reason);
    setNewBlacklistContact({ phoneNumber: "", reason: "" });
    setShowAddBlacklistModal(false);
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="spinner-border text-primary"></div>
      </div>
    );
  }

  return (
    <Container className="mainWrapper px-3" style={{ paddingTop: "80px", paddingBottom: "90px" }}>
      {/* Top Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="text-light fs-5 mb-0">
          <i className="far fa-address-book me-2 text-success"></i>
          Contacts Manager
        </h2>
        {activeTab === "all" && (
          <Button
            onClick={syncContacts}
            disabled={syncing}
            variant="primary"
            size="sm"
            className="d-flex align-items-center gap-1"
          >
            {syncing ? (
              <>
                <span className="spinner-border spinner-border-sm"></span>
                <span>Syncing...</span>
              </>
            ) : (
              <>
                <i className="fas fa-sync-alt"></i>
                <span>Import Contacts</span>
              </>
            )}
          </Button>
        )}
        {activeTab === "trusted" && (
          <Button
            variant="success"
            size="sm"
            onClick={() => setShowAddWhitelistModal(true)}
            className="d-flex align-items-center gap-1"
          >
            <i className="fas fa-plus"></i>
            <span>Add Trusted</span>
          </Button>
        )}
        {activeTab === "blocked" && (
          <Button
            variant="danger"
            size="sm"
            onClick={() => setShowAddBlacklistModal(true)}
            className="d-flex align-items-center gap-1"
          >
            <i className="fas fa-ban"></i>
            <span>Block Number</span>
          </Button>
        )}
      </div>

      {/* Segmented Tab Navigation */}
      <div className="d-flex justify-content-center mb-3">
        <ButtonGroup className="w-100 shadow-sm" style={{ maxWidth: "500px" }}>
          <Button
            variant={activeTab === "all" ? "primary" : "outline-secondary"}
            onClick={() => setActiveTab("all")}
            className="fw-medium py-2"
            style={{ fontSize: "0.85rem" }}
          >
            All ({contacts.length})
          </Button>
          <Button
            variant={activeTab === "trusted" ? "success" : "outline-secondary"}
            onClick={() => setActiveTab("trusted")}
            className="fw-medium py-2"
            style={{ fontSize: "0.85rem" }}
          >
            <i className="fas fa-shield-alt me-1"></i>
            Trusted ({whitelist.length})
          </Button>
          <Button
            variant={activeTab === "blocked" ? "danger" : "outline-secondary"}
            onClick={() => setActiveTab("blocked")}
            className="fw-medium py-2"
            style={{ fontSize: "0.85rem" }}
          >
            <i className="fas fa-ban me-1"></i>
            Blocked ({blacklist.length})
          </Button>
        </ButtonGroup>
      </div>

      {/* Feedback Message */}
      {message && (
        <Alert variant={messageType} dismissible onClose={() => setMessage("")}>
          {message}
        </Alert>
      )}

      {/* TAB 1: ALL CONTACTS (Imported) */}
      {activeTab === "all" && (
        <div>
          <div className="mb-3">
            <InputGroup>
              <InputGroup.Text>
                <i className="fas fa-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search contacts by name or number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Button
                variant="primary"
                onClick={syncContacts}
                disabled={syncing}
                title="Sync Contacts from Phone"
                className="d-flex align-items-center gap-1 px-3"
              >
                <i className={`fas fa-sync-alt ${syncing ? "fa-spin" : ""}`}></i>
                <span>{syncing ? "Syncing..." : "Sync"}</span>
              </Button>
            </InputGroup>
          </div>

          {contacts.length === 0 ? (
            <div className="text-center py-5">
              <i className="far fa-address-book fa-3x text-muted mb-3"></i>
              <p className="text-muted">No contacts imported yet.</p>
              <Button variant="primary" onClick={syncContacts} disabled={syncing}>
                Import Phone Contacts
              </Button>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="text-center py-5 text-muted">
              No contacts matched "{searchTerm}"
            </div>
          ) : (
            <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
              {filteredContacts.map((contact, idx) => {
                const norm = normalizePhone(contact.phone);
                const isTrusted = whitelist.some((w) => normalizePhone(w.phoneNumber) === norm);
                const isBlocked = blacklist.some((b) => normalizePhone(b.phoneNumber) === norm);

                return (
                  <div key={idx} className="col">
                    <Card className="h-100 shadow-sm border-0 bg-dark text-light">
                      <Card.Body className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <h6 className="card-title mb-0 text-white fw-bold">
                            {contact.name || "Unknown"}
                          </h6>
                          {isTrusted && <Badge bg="success">Trusted</Badge>}
                          {isBlocked && <Badge bg="danger">Blocked</Badge>}
                        </div>
                        <p className="text-muted small mb-3">{contact.phone}</p>

                        {/* Action Buttons */}
                        <div className="d-flex gap-2 mt-auto">
                          <Button
                            variant="success"
                            size="sm"
                            className="flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                            onClick={() => callContact(contact.phone)}
                          >
                            <i className="fas fa-phone-alt"></i>
                            <span>Call</span>
                          </Button>
                          <Button
                            variant="primary"
                            size="sm"
                            className="flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                            onClick={() => textContact(contact.phone)}
                          >
                            <i className="fas fa-comment-dots"></i>
                            <span>Text</span>
                          </Button>
                        </div>

                        {/* Security Actions */}
                        <div className="d-flex gap-2 mt-2">
                          {!isTrusted && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="w-100"
                              style={{ fontSize: "0.75rem" }}
                              onClick={() => addToWhitelist(contact.phone, contact.name)}
                            >
                              <i className="fas fa-check-circle me-1"></i> Trust
                            </Button>
                          )}
                          {!isBlocked && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="w-100"
                              style={{ fontSize: "0.75rem" }}
                              onClick={() => addToBlacklist(contact.phone)}
                            >
                              <i className="fas fa-ban me-1"></i> Block
                            </Button>
                          )}
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* TAB 2: TRUSTED (WHITELIST) */}
      {activeTab === "trusted" && (
        <Card className="shadow-sm border-0 bg-dark text-light">
          <Card.Body>
            {whitelist.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-shield-alt fa-3x text-success mb-3"></i>
                <p>No trusted contacts added yet.</p>
                <p className="small">Trusted contacts bypass call and text screening automatically.</p>
                <Button variant="success" size="sm" onClick={() => setShowAddWhitelistModal(true)}>
                  Add First Trusted Contact
                </Button>
              </div>
            ) : (
              <Table responsive hover variant="dark" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone Number</th>
                    <th>Last Contact</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {whitelist.map((w) => (
                    <tr key={w._id || w.phoneNumber}>
                      <td className="fw-semibold text-white">{w.name || "Unknown"}</td>
                      <td>{w.phoneNumber}</td>
                      <td className="text-muted small">
                        {w.lastCalled ? new Date(w.lastCalled).toLocaleDateString() : "Never"}
                      </td>
                      <td className="text-end">
                        <Button
                          variant="success"
                          size="sm"
                          className="me-1"
                          onClick={() => callContact(w.phoneNumber)}
                          title="Call"
                        >
                          <i className="fas fa-phone-alt"></i>
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          className="me-1"
                          onClick={() => textContact(w.phoneNumber)}
                          title="Text"
                        >
                          <i className="fas fa-comment-dots"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeWhitelist(w.phoneNumber)}
                          title="Remove from Trusted"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* TAB 3: BLOCKED (BLACKLIST) */}
      {activeTab === "blocked" && (
        <Card className="shadow-sm border-0 bg-dark text-light">
          <Card.Body>
            {blacklist.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-check-circle fa-3x text-muted mb-3"></i>
                <p>Your blocked list is empty.</p>
                <p className="small">Calls and texts from blocked numbers are dropped silently.</p>
              </div>
            ) : (
              <Table responsive hover variant="dark" className="align-middle mb-0">
                <thead>
                  <tr>
                    <th>Phone Number</th>
                    <th>Reason</th>
                    <th>Blocked Date</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blacklist.map((b) => (
                    <tr key={b._id || b.phoneNumber}>
                      <td className="fw-bold text-danger">{b.phoneNumber}</td>
                      <td className="text-muted small">{b.reason || "Manual Block"}</td>
                      <td className="text-muted small">
                        {b.createdAt ? new Date(b.createdAt).toLocaleDateString() : "N/A"}
                      </td>
                      <td className="text-end">
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-2"
                          onClick={() => addToWhitelist(b.phoneNumber)}
                        >
                          <i className="fas fa-shield-alt me-1"></i> Trust
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => removeBlacklist(b.phoneNumber)}
                        >
                          Unblock
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal: Add Trusted Contact */}
      <Modal
        show={showAddWhitelistModal}
        onHide={() => setShowAddWhitelistModal(false)}
        centered
        contentClassName="bg-dark text-light"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title className="fs-6">Add Trusted Contact</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleManualAddWhitelist}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Contact Name (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. John Doe"
                value={newWhitelistContact.name}
                onChange={(e) =>
                  setNewWhitelistContact({ ...newWhitelistContact, name: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="tel"
                placeholder="+12345678900"
                required
                value={newWhitelistContact.phoneNumber}
                onChange={(e) =>
                  setNewWhitelistContact({ ...newWhitelistContact, phoneNumber: e.target.value })
                }
              />
              <Form.Text className="text-muted">
                Include country code (e.g., +1 for US/Canada)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddWhitelistModal(false)}>
              Cancel
            </Button>
            <Button variant="success" type="submit">
              Save Trusted Contact
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Add Blocked Contact */}
      <Modal
        show={showAddBlacklistModal}
        onHide={() => setShowAddBlacklistModal(false)}
        centered
        contentClassName="bg-dark text-light"
      >
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title className="fs-6">Block Phone Number</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleManualAddBlacklist}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Phone Number to Block</Form.Label>
              <Form.Control
                type="tel"
                placeholder="+12345678900"
                required
                value={newBlacklistContact.phoneNumber}
                onChange={(e) =>
                  setNewBlacklistContact({ ...newBlacklistContact, phoneNumber: e.target.value })
                }
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Reason (Optional)</Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. Spam caller"
                value={newBlacklistContact.reason}
                onChange={(e) =>
                  setNewBlacklistContact({ ...newBlacklistContact, reason: e.target.value })
                }
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddBlacklistModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" type="submit">
              Block Number
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default Contacts;
